'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { useTechnology } from '@/hooks/technology';
import { useToast } from '@/hooks/use-toast';
import type { ISerialPort } from '@/hooks/useSerialPort';
import { fetchWithAuth, getUserFromToken } from '@/lib/api';
import type { IConfigurationLog } from '@/types/production/configuration-log';
import type { IConfigurationProfile } from '@/types/production/configuration-profile';
import type { Device } from '@/types/production/device';
import type { ITechnology } from '@/types/production/tool';

namespace Namespace {
  export interface UseConfigurationProps {
    technology: ITechnology | null;
    configurationProfile?: IConfigurationProfile | null;
  }

  export interface Detected {
    port: ISerialPort;
    equipment?: Equipment | undefined;
    status: 'fully_identified' | 'partially_identified' | 'not_identified';
  }

  interface Equipment {
    imei?: string | undefined;
    iccid?: string | undefined;
    firmware?: string | undefined;
    serial?: string | undefined;
    lora_keys?: Partial<Device['Equipment']['lora_keys']>;
  }

  export interface ConfigurationLogInput {
    equipment: {
      serial: string;
      firmware: string;
      imei?: string;
      iccid?: string;
      lora_keys?: {
        dev_eui?: string;
        app_eui?: string;
        app_key?: string;
        dev_addr?: string;
        nwk_s_key?: string;
        app_s_key?: string;
      };
    };
    technology: {
      id: string;
      system_name: string;
    };
    user: {
      id: string;
      name: string;
    };
    status: boolean;
    desired_profile: {
      id: string;
      name: string;
      config: Record<string, unknown>;
      type: string;
    };
    applied_profile?: Record<string, unknown>;
    checked: boolean;
    checked_at?: Date | string;
    messages: {
      key: string;
      request: string;
      response?: string | null;
    }[];
    init_time: number;
    end_time: number;
  }

  export interface Configuration extends ConfigurationLogInput {
    id: string;
    created_at: Date | string;
  }
}

// Helper function to transform lora_keys from frontend format to backend format
function transformLoraKeys(
  lora_keys?: Partial<Device['Equipment']['lora_keys']>,
): Namespace.ConfigurationLogInput['equipment']['lora_keys'] {
  if (!lora_keys) return undefined;

  return {
    dev_eui: lora_keys.device_eui,
    app_eui: lora_keys.application_eui,
    app_key: lora_keys.application_key,
    dev_addr: lora_keys.device_address,
    nwk_s_key: lora_keys.network_session_key,
    app_s_key: lora_keys.application_session_key,
  };
}

type ConfigurationLogCreate = Namespace.ConfigurationLogInput;

export const useConfiguration = (props: Namespace.UseConfigurationProps) => {
  const { technology, configurationProfile } = props;

  const navigate = useNavigate();
  const { toast } = useToast();

  const [detected, setDetected] = useState<Namespace.Detected[]>([]);
  const detectedKey = useMemo(() => {
    return detected
      .map((d) => d.equipment?.serial ?? '')
      .filter((s) => s.length > 0)
      .sort()
      .join('|');
  }, [detected]);
  const [isDetecting, setIsDetecting] = useState<boolean>(false);
  // Use ref to prevent concurrent detections (synchronous check)
  const isDetectingRef = useRef<boolean>(false);

  const [configured, setConfigured] = useState<Namespace.Configuration[]>([]);
  const [isConfiguring, setIsConfiguring] = useState<boolean>(false);
  // Use ref to prevent detection during configuration (synchronous check)
  const isConfiguringRef = useRef<boolean>(false);

  const [autoConfigurationEnabled, setAutoConfigurationEnabled] = useState<boolean>(false);

  const toggleAutoConfiguration = (checked: boolean) => setAutoConfigurationEnabled(checked);

  const [redirectToCheckEnabled, setRedirectToCheckEnabled] = useState<boolean>(false);

  const toggleRedirectToCheck = (checked: boolean) => setRedirectToCheckEnabled(checked);

  const redirectToCheck = (configurationLog: IConfigurationLog[]) => {
    if (!technology?.id) return;
    const params = new URLSearchParams();
    params.set('technology_id', technology.id);
    configurationLog.forEach(({ id }) => {
      params.append('configuration_log_id', id);
    });
    params.set('auto_checking', 'true');

    navigate(`/production/tools/check-configuration?${params.toString()}`);
  };

  // hook that handle interactions with devices
  const { ports, handleDetection, handleConfiguration, requestPort, isIdentified } =
    useTechnology(technology);

  // function that handle the configuration process, check if the process was successful and save result on database
  const configure = async (
    detected: Namespace.Detected[],
    configuration_profile: IConfigurationProfile,
  ) => {
    isConfiguringRef.current = true;
    setIsConfiguring(true);

    try {
      const validDetected = detected.filter(
        (
          i,
        ): i is (typeof detected)[number] & {
          equipment: { serial: string; firmware: string };
        } => Boolean(i.equipment?.serial && i.equipment?.firmware),
      );

      if (!validDetected.length) {
        toast({
          title: 'Atenção!',
          description: 'Nenhum dispositivo válido foi detectado para configuração.',
        });
        return;
      }

      const configurationResult = await handleConfiguration(validDetected, configuration_profile);

      // check if each message sent has response and configured to the desired profile
      const result = configurationResult
        .map(({ messages, status, equipment, applied_profile, init_time, end_time }) => {
          const user = getUserFromToken();
          if (!user) {
            throw new Error('User not authenticated');
          }

          const configuration_log: ConfigurationLogCreate = {
            equipment: {
              ...equipment,
              lora_keys: transformLoraKeys(equipment.lora_keys),
            },
            checked: false,
            init_time,
            end_time,
            status,
            applied_profile: applied_profile as any,
            desired_profile: {
              type: configuration_profile.type,
              id: configuration_profile.id,
              name: configuration_profile.name,
              config: configuration_profile.config as any,
            },
            technology: {
              id: technology?.id ?? '',
              system_name: technology?.name.system ?? '',
            },
            messages: messages.map((msg: any) => ({
              key: msg.key,
              request: msg.request,
              response:
                msg.response == null
                  ? null
                  : typeof msg.response === 'object'
                    ? JSON.stringify(msg.response)
                    : String(msg.response),
            })),
            user,
          };

          return configuration_log;
        })
        .filter((el): el is NonNullable<typeof el> => el !== undefined);

      // save result on database
      const res = await fetchWithAuth('/api/production/configuration-logs/batch', {
        method: 'POST',
        body: JSON.stringify({ logs: result }),
      });
      if (!res.ok) {
        const errorText = await res.text();
        console.error('[ERROR] API Response:', errorText);
        throw new Error('Erro ao salvar os logs');
      }
      const { data: dataSavedOnDb } = await res.json();
      const logs: IConfigurationLog[] = dataSavedOnDb ?? [];

      // update state with configuration process result
      setConfigured((prev) => prev.concat(logs));

      if (logs.every((log) => !!log.status)) {
        toast({
          title: 'Sucesso!',
          description: 'Processo de configuração finalizado.',
        });
      } else {
        toast({
          title: 'Error!',
          description: 'Ocorreu um erro durante a configuração.',
        });
      }

      redirectToCheckEnabled && redirectToCheck(logs);
    } catch (error) {
      toast({
        title: 'Error!',
        description: 'Ocorreu um erro durante a configuração.',
        variant: 'destructive',
      });
      console.error('[ERROR] configure use-configuration', error);
    } finally {
      isConfiguringRef.current = false;
      setIsConfiguring(false);
    }
  };

  // useEffect used to identify devices when connected via serial ports
  useEffect(() => {
    const interval = setInterval(async () => {
      if (isConfiguringRef.current) return;

      // Use ref for synchronous check - prevents concurrent detections
      if (!isDetectingRef.current && ports.length) {
        isDetectingRef.current = true;
        setIsDetecting(true);
        try {
          const detected = (await handleDetection(ports)).filter((d) => d.response?.serial);

          setDetected(() => {
            const map = new Map();

            for (const { port, response } of detected) {
              map.set(response?.serial, {
                port,
                equipment: response,
                status: !response ? 'not_identified' : isIdentified(response),
              });
            }

            return Array.from(new Set(map.values()));
          });
        } finally {
          isDetectingRef.current = false;
          setIsDetecting(false);
        }
      } else if (!isDetectingRef.current && !ports.length) {
        setDetected([]);
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [ports, handleDetection, isIdentified]);

  useEffect(() => {
    (async () => {
      if (detectedKey.length > 0 && autoConfigurationEnabled && configurationProfile) {
        await configure(detected, configurationProfile);
      }
    })();
    // biome-ignore lint/correctness/useExhaustiveDependencies: configure recreates on every render and would cause infinite loop
  }, [detectedKey, configurationProfile, autoConfigurationEnabled, configure, detected]);

  return {
    configured,
    detected,
    configure,
    requestPort,
    isConfiguring,
    isDetecting,
    toggleAutoConfiguration,
    autoConfigurationEnabled,
    redirectToCheckEnabled,
    toggleRedirectToCheck,
    redirectToCheck,
  };
};

export default useConfiguration;
